import { useSearch } from "@tanstack/react-router";
import { tableSearchSchema, useTableUrlPersistence } from "@/hooks/useTableUrlPersistence";
import {
  LOCATION_STATUS_OPTIONS,
  LOCATION_TYPE_OPTIONS,
  LocationQueryKeys,
} from "@/api/locations/locations.queries";
import type {
  ILocationApiResponse,
  ILocationQuickViewData,
  ILocations,
  IWorkOrders,
} from "@/api/locations/locations.queries.type";
import type { RouterContextType } from "@/api/permissions/permissions.api";
import { MODULE_KEYS_MAP } from "@/api/permissions/permissions.types";
import AssetsModal from "@/components/features/ClientOnboarding/AssetsInfo/AssetsModal/AssetsModal";
import ContactsModal from "@/components/features/ClientOnboarding/Contacts/ContactsModal/ContactsModal";
import AddOrEditLocationsModal from "@/components/features/ClientOnboarding/LocationInfo/AddOrEditLocations/AddOrEditLocationsModal";
import {
  LocationQuickViewDrawer,
  type DrawerBadge,
} from "@/components/features/LocationDetails/LocationQuickViewDrawer/LocationQuickViewDrawer";
import { LocationStatusModal } from "@/components/features/LocationDetails/LocationStatusModals/LocationStatusModal";
import { Badge } from "@onengine/component-library";
import Table from "@/components/ui/Table/Table";
import { StaticSortableTable } from "@/components/ui/Table/StaticSortableTable";
import TableActionsMenu from "@/components/ui/TableActionsMenu/TableActionsMenu";
import type { TColumn } from "@/components/ui/TableCard/TableCard";
import TableCellWithAvatar from "@/components/ui/TableCellWithAvatar/TableCellWithAvatar";
import {
  useLocationListWithFilters,
  useLocationQuickViewDetails,
  useLocationVisitsTable,
} from "@/hooks/locations/useLocationListQueries";
import { useBreadcrumb } from "@/hooks/useBreadcrumb";
import {
  getLocationStatusBadge,
  getLocationTypeBadge,
  getVisitTypeBadgeColors,
} from "@/utils/badgeConfig";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { buildTableQueryOptions } from "@/utils/buildTableQueryOptions";
import { formatCurrency } from "@/utils/formatCurrency";
import { getRouteIcon } from "@/utils/getRouteIconComponent";
import { formatLocationVisitDateTime } from "@/utils/timeConverter";
import { Box, Flex, Stack, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconAddressBook,
  IconCircleCheck,
  IconCircleCheckFilled,
  IconCircleMinus,
  IconEdit,
  IconEye,
  IconPackage,
} from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useEffect, useMemo, useState } from "react";
import { z } from "zod";
import styles from "./locations.module.scss";

const locationsSearchSchema = tableSearchSchema.extend({
  quickViewLocationId: z.string().optional().catch(undefined),
});

type NextUpcomingVisit = {
  visitId: string;
  workOrderId: string;
  scheduledTime: string;
  workOrderNumber: string;
  workOrder: string;
  visitType: string;
  locationAddress: {
    city: string;
    state: string;
    street: string;
    zip?: string;
  };
  providers: [
    {
      providerId: string;
      providerName: string;
      providerStatus: string;
      providerType: string;
      scheduleTime: string;
      visitStatus: string;
    },
  ];
};

function UpcomingVisitsTable({
  data,
  isLoading,
  selectedLocationId,
}: {
  data: NextUpcomingVisit[];
  isLoading: boolean;
  selectedLocationId: string | null;
}) {
  const navigate = useNavigate();

  const columns = useMemo<ColumnDef<NextUpcomingVisit, unknown>[]>(
    () => [
      {
        accessorKey: "scheduledTime",
        header: "Visit Time",
        cell: (props) => {
          const result = formatLocationVisitDateTime(props.row.original.scheduledTime);
          return (
            <Stack gap={"var(--spacing-2x-small)"}>
              <Text size="sm" fw={"var(--font-weight-regular)"}>
                {result.dayLabel}
              </Text>
              <Text size="sm" fw={"var(--font-weight-regular)"}>
                {result.timeLabel}
              </Text>
            </Stack>
          );
        },
      },
      {
        accessorKey: "workOrderNumber",
        header: "Work Order #",
        cell: (props) => (
          <Text size="sm" fw={"var(--font-weight-regular)"} ta="center">
            wo-{props.getValue() as string}
          </Text>
        ),
      },
      {
        accessorKey: "visitType",
        header: "Visit Type",
        accessorFn: (row) => row.visitType ?? "",
        cell: (props) => {
          const visitType = props.getValue() as string;
          return (
            <Badge size="medium" {...getVisitTypeBadgeColors(visitType)}>
              {visitType}
            </Badge>
          );
        },
      },
      {
        accessorKey: "locationAddress",
        header: "Location Name",
        enableSorting: false,
        cell: (props) => {
          const addr = props.row.original.locationAddress;
          if (!addr) return <Text size="sm">—</Text>;
          const { city, state, street, zip } = addr;
          return <Text size="sm">{`${street}, ${city}, ${state} ${zip}`}</Text>;
        },
      },
      {
        accessorKey: "providers",
        header: "Assigned SP",
        enableSorting: false,
        cell: (props) => {
          const providerName = props.row.original.providers?.[0]?.providerName || "—";
          return providerName ? (
            <TableCellWithAvatar name={providerName} />
          ) : (
            <Text size="10px">-</Text>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <TableActionsMenu
            options={[
              {
                btnText: "Edit",
                onClickHandler: () =>
                  navigate({
                    to: "/work-orders/$workOrderId/$visitId/edit",
                    params: {
                      workOrderId: row.original.workOrderId,
                      visitId: row.original.visitId,
                    },
                    search: selectedLocationId
                      ? { returnTo: "locations" as const, returnId: selectedLocationId }
                      : {},
                  }),
              },
            ]}
            isMenuDisabled={false}
            menuId={1}
          />
        ),
      },
    ],
    [navigate, selectedLocationId],
  );

  return (
    <StaticSortableTable
      data={data}
      columns={columns}
      maxRows={5}
      columnPinning={{ left: [], right: ["actions"] }}
      isLoading={isLoading}
    />
  );
}

export const Route = createFileRoute("/_authenticated/locations/")({
  component: Locations,
  validateSearch: locationsSearchSchema.parse,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

const LOCATIONS_VIEW_NAME = "locationList";

function Locations() {
  const navigate = useNavigate();
  const searchParams = useSearch({ from: "/_authenticated/locations/" });

  useBreadcrumb([
    {
      title: "Locations",
      href: "/locations",
      icon: getRouteIcon(MODULE_KEYS_MAP.locations),
    },
  ]);

  const [markAsInoperableModalOpened, markAsInoperableModalHandlers] = useDisclosure(false);
  const [locationsDetailsDrawerOpened, locationDetailsDrawerHandlers] = useDisclosure(false);
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  useEffect(() => {
    if (!searchParams.quickViewLocationId) return;
    setSelectedLocationId(searchParams.quickViewLocationId);
    locationDetailsDrawerHandlers.open();
    navigate({
      to: "/locations",
      search: (prev) => {
        const next = { ...prev };
        delete next.quickViewLocationId;
        return next;
      },
      replace: true,
    });
  }, [searchParams.quickViewLocationId]);
  const [locationModalOpened, locationModalHandlers] = useDisclosure(false);
  const [assetsModalOpened, assetsModalHandlers] = useDisclosure(false);
  const [contactsModalOpened, contactsModalHandlers] = useDisclosure(false);

  const [modalLocationId, setModalLocationId] = useState<string | null>(null);
  const [modalClientId, setModalClientId] = useState<string | undefined>(undefined);
  const [presetLocationsManaged, setPresetLocationsManaged] = useState<any[]>([]);
  const [locationStatusMeta, setLocationStatusMeta] = useState<{
    siteId: string | null;
    currentStatus: "operable" | "inoperable";
  }>({ siteId: null, currentStatus: "operable" });

  const locationColumns = useMemo<ColumnDef<ILocations, unknown>[]>(
    () => [
      {
        accessorKey: "siteId",
        header: "Site ID",
        accessorFn: (row) => row.siteId,
        cell: ({ getValue }) => (
          <Text size="sm" fw={"var(--font-weight-medium)"}>
            {getValue() as string}
          </Text>
        ),
      },
      {
        accessorKey: "status",
        header: "Status",
        accessorFn: (row) => row.status.label,
        cell: ({ row }) => {
          const status = row.original.status.value;
          return <Badge size="medium" {...getLocationStatusBadge(status)} />;
        },
      },
      // {
      //   accessorKey: "compliance",
      //   header: "Compliance",
      //   cell: (props) => {
      //     const compliance = props.getValue() as string;
      //     // Map to the expected format for badge colors
      //     return (
      //       <Badge
      //         size="medium"
      //         variant="plain"
      //         text={""}
      //         iconLeft={getLocationStatusBadge(compliance).iconLeft}
      //       />
      //     );
      //   },
      // },
      {
        accessorKey: "locationType",
        header: "Location Type",
        accessorFn: (row) => row.locationType,
        cell: ({ getValue }) => {
          const type = getValue() as string;
          return <Badge size="medium" {...getLocationTypeBadge(type)} />;
        },
      },
      {
        accessorKey: "clientName",
        header: "Client Name",
        accessorFn: (row) => row.clientName,
        cell: ({ getValue }) => (
          <Text size="sm" ta="left">
            {getValue() as string}
          </Text>
        ),
      },
      {
        accessorKey: "brand",
        header: "Brand",
        accessorFn: (row) => row.brand,
        cell: ({ getValue }) => (
          <Text size="sm" ta="left">
            {getValue() as string}
          </Text>
        ),
      },
      {
        accessorKey: "activeWork",
        header: "Active Work",
        accessorFn: (row) => row.activeWork,
        cell: ({ getValue }) => {
          const name = getValue() as string | null;
          return name ? (
            <Text size="sm" ta="left">
              {getValue() as string}
            </Text>
          ) : (
            <Text size="10px">—</Text>
          );
        },
      },
      {
        accessorKey: "timezone",
        header: "Timezone",
        sortDescFirst: false,
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          return value ? (
            <Text size="sm" ta="left">
              {value}
            </Text>
          ) : (
            <Text size="10px">—</Text>
          );
        },
      },
      {
        accessorKey: "address",
        header: "Address",
        accessorFn: (row) => row.address,
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          return value ? (
            <Text size="sm" ta="left" truncate="end" maw={200}>
              {value}
            </Text>
          ) : (
            <Text size="10px">—</Text>
          );
        },
      },
      {
        accessorKey: "zipcode",
        header: "Zip Code",
        accessorFn: (row) => row.zipcode,
        cell: ({ getValue }) => {
          const value = getValue() as string | null;
          return value ? (
            <Text size="sm" ta="left">
              {value}
            </Text>
          ) : (
            <Text size="10px">—</Text>
          );
        },
      },
      {
        accessorKey: "actions",
        header: "",
        cell: ({ row }) => (
          <TableActionsMenu
            options={[
              {
                btnText: "View Details",
                onClickHandler: () => {
                  setModalLocationId(row.original.id);
                  navigate({
                    to: "/locations/$id",
                    params: { id: row.original.id },
                  });
                  locationDetailsDrawerHandlers.close();
                  // locationModalHandlers.open();
                },
                iconLeft: <IconEye size={16} />,
              },
              {
                btnText: "Edit Location Info",
                onClickHandler: () => {
                  setModalLocationId(row.original.id);
                  setModalClientId(row.original.clientId);
                  locationModalHandlers.open();
                },
                iconLeft: <IconEdit size={16} />,
              },
              {
                type: "divider",
              },
              {
                btnText: "Add Asset",
                onClickHandler: () => {
                  setModalLocationId(row.original.id);
                  setModalClientId(row.original.clientId);
                  assetsModalHandlers.open();
                },
                iconLeft: <IconPackage size={16} />,
              },
              {
                btnText: "Add Contact",
                onClickHandler: () => {
                  setModalLocationId(row.original.id);
                  setModalClientId(row.original.clientId);
                  setPresetLocationsManaged([
                    {
                      id: row.original.id,
                      siteId: row.original.siteId,
                      address: {
                        address1: row.original.address,
                        address2: null,
                        city: "",
                        state: "",
                        zipcode: row.original.zipcode,
                      },
                    },
                  ]);
                  contactsModalHandlers.open();
                },
                iconLeft: <IconAddressBook size={16} />,
              },
              {
                type: "divider",
              },
              {
                btnText:
                  row.original.status.value === "operable"
                    ? "Mark as Inoperable"
                    : "Mark as Operable",
                onClickHandler: () => {
                  setModalLocationId(row.original.id);
                  setLocationStatusMeta({
                    siteId: row.original.siteId,
                    currentStatus: row.original.status.value,
                  });
                  markAsInoperableModalHandlers.open();
                },
                color:
                  row.original.status.value === "operable"
                    ? "var(--text-danger-default)"
                    : "var(--text-success-default)",
                iconLeft:
                  row.original.status.value === "operable" ? (
                    <IconCircleMinus size={16} />
                  ) : (
                    <IconCircleCheck size={16} />
                  ),
              },
            ]}
            isMenuDisabled={false}
            menuId={1}
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
    ],
    [],
  );

  const [tableColumns, setTableColumns] = useState<TColumn[]>([
    {
      id: "siteId",
      label: "Site Id",
      visible: true,
      sortable: true,
      searchParams: {
        type: "locations",
        fields: ["siteId"],
        limit: 10,
        key: "siteId",
        filterByKey: "siteId",
      },
    },
    {
      id: "status",
      label: "Status",
      visible: true,
      sortable: true,
      searchParams: {
        type: "locations",
        fields: ["value"],
        limit: 10,
        key: "status",
        filterByKey: "status",
      },
    },
    {
      id: "locationType",
      label: "Location Type",
      visible: true,
      sortable: true,
      searchParams: {
        type: "locations",
        fields: ["label"],
        limit: 10,
        key: "locationsType",
        filterByKey: "locationType",
      },
    },
    {
      id: "clientName",
      label: "Client Name",
      visible: true,
      sortable: true,
      searchParams: {
        type: "locations",
        fields: ["businessName"],
        limit: 10,
        key: "clientName",
        filterByKey: "clientName",
      },
    },
    {
      id: "brand",
      label: "Brand",
      visible: true,
      sortable: true,
      searchParams: {
        type: "locations",
        fields: ["label"],
        limit: 10,
        key: "brand",
        filterByKey: "brand",
        filterValueKey: "id",
      },
    },
    {
      id: "activeWork",
      label: "Active Work",
      visible: true,
      sortable: true,
      searchParams: {
        type: "locations",
        fields: ["value"],
        limit: 10,
        key: "activeWork",
        filterByKey: "activeWorkOrdersCount",
      },
    },
    {
      id: "timezone",
      label: "Timezone",
      visible: true,
      sortable: true,
      searchParams: {
        type: "locations",
        fields: ["value"],
        limit: 10,
        key: "timezone",
        filterByKey: "timezone",
      },
    },
    {
      id: "address",
      label: "Address",
      visible: true,
      sortable: true,
      searchParams: {
        type: "locations",
        fields: ["label"],
        limit: 10,
        key: "address",
        filterByKey: "address",
        filterValueKey: "id",
      },
    },
    {
      id: "zipcode",
      label: "Zip Code",
      visible: true,
      sortable: true,
      searchParams: {
        type: "locations",
        fields: ["label"],
        limit: 10,
        key: "zipcodes",
        filterByKey: "zipcode",
      },
    },
  ]);

  const {
    page,
    setPage,
    pageSize,
    setPageSize,
    setSearchValue,
    sorting,
    setSorting,
    debouncedSearch,
    filters,
    viewName,
    filtersKey,
    applyFilters,
    resetFilters,
    isPrefsLoading,
  } = useTableUrlPersistence({
    viewName: LOCATIONS_VIEW_NAME,
    filterKeyToLabel: LOCATIONS_FILTER_KEY_TO_LABEL,
    searchParams,
    navigate,
    tableColumns,
  });

  const queryOptions = useMemo(
    () =>
      buildTableQueryOptions({
        page,
        pageSize,
        search: debouncedSearch,
        sorting,
        filters,
        tableColumns,
        getSortByField,
      }),
    [page, pageSize, debouncedSearch, sorting, filtersKey, tableColumns],
  );

  const {
    data: locationData,
    isFetching,
    isLoading,
    isPlaceholderData,
  } = useLocationListWithFilters(queryOptions);
  const { data: locationQuickViewData, isFetching: overviewFetching } =
    useLocationQuickViewDetails(selectedLocationId);

  const { data: locationVisitsTableData, isLoading: locationVisitsTableLoading } =
    useLocationVisitsTable(selectedLocationId);

  const showLoadingOverlay = isLoading || (isFetching && isPlaceholderData) || isPrefsLoading;

  const accessFeatureBadges = [
    {
      text: "Onsite Parking Available",
      variant: "plain" as const,
      iconLeft: <IconCircleCheckFilled color="var(--icon-success-default)" size={16} />,
    },
    {
      text: "Loading Dock Available",
      variant: "plain" as const,
      iconLeft: <IconCircleCheckFilled color="var(--icon-success-default)" size={16} />,
    },
    {
      text: "Roof Access Available",
      variant: "plain" as const,
      iconLeft: <IconCircleCheckFilled color="var(--icon-success-default)" size={16} />,
    },
  ];

  const locationBadges: DrawerBadge[] = locationQuickViewData
    ? [
        {
          variant: "plain",
          iconLeft:
            locationQuickViewData.operableStatus?.toLowerCase() === "operable" ? (
              <IconCircleCheckFilled color="var(--icon-success-default)" size={16} />
            ) : (
              <IconCircleMinus size={16} />
            ),
          children: locationQuickViewData.operableStatus,
        },
      ]
    : [];

  const locationMetrics = locationQuickViewData
    ? Object.entries(mapConfig).map(([label, config]) => ({
        label,
        value: String(
          locationQuickViewData.metrics[
            config.metricKey as keyof typeof locationQuickViewData.metrics
          ],
        ),
        onExternalLinkClick: () => {
          navigate({
            to: "/locations/$id",
            params: { id: selectedLocationId || "" },
            search: { tab: config.tab as "assets" | "contacts" | "documents" },
          });
        },
      }))
    : [];

  const workOrderSummary = locationQuickViewData
    ? workOrdersSummary.map(({ label, key, tab }) => ({
        label,
        value: String(locationQuickViewData.workOrders[key]),
        onClick: () => {
          locationDetailsDrawerHandlers.close();
          navigate({
            to: "/work-orders",
            search: {
              tab,
              ...(selectedLocationId
                ? {
                    filterBy: JSON.stringify({
                      locationId: [
                        {
                          id: selectedLocationId,
                          value: locationQuickViewData.locationAddress.fullAddress,
                        },
                      ],
                    }),
                  }
                : {}),
            },
          });
        },
      }))
    : [];

  const quickViewInvoices = locationQuickViewData?.invoices ?? null;
  const invoiceSummary = quickViewInvoices
    ? paymentsAndInvoicesSummary.map(({ key, subtitle, isAmount }) => ({
        title: isAmount
          ? formatCurrency(Number(quickViewInvoices[key]) || 0, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })
          : String(Number(quickViewInvoices[key]) || 0).padStart(2, "0"),
        subtitle,
      }))
    : [];

  const tableData = useMemo(() => {
    if (!locationData?.data?.data) {
      return [];
    }
    return locationData.data.data.map(mapLocationApiToLocation);
  }, [locationData]);

  const filterData = useMemo(() => {
    const result: Record<string, string[]> = {};

    tableColumns.forEach((col) => {
      if (col.id === "status") {
        result[col.id] = LOCATION_STATUS_OPTIONS.map((opt) => opt.label);
      } else if (col.id === "clientType") {
        result[col.id] = LOCATION_TYPE_OPTIONS.map((opt) => opt.label);
      } else if (col.searchParams) {
        const uniqueValues = new Set<string>();
        tableData?.forEach((loc: any) => {
          const value = loc[col.id as keyof ILocations];
          if (value !== undefined && value !== null) {
            uniqueValues.add(String(value));
          }
        });
        result[col.id] = Array.from(uniqueValues).sort();
      }
    });

    return result;
  }, [tableColumns, tableData]);

  const handleSortChange = (columnId: string | null, direction: "asc" | "desc") => {
    if (columnId) {
      setSorting([{ id: columnId, desc: direction === "desc" }]);
    } else {
      setSorting([]);
    }
  };

  return (
    <Box>
      <Flex direction="column" className={styles.container}>
        <Flex direction="column" gap="var(--spacing-large)">
          <Flex justify="space-between" align="center">
            <Title order={3} className={styles.title}>
              Locations
            </Title>
          </Flex>
          <Table
            data={tableData}
            columns={locationColumns}
            sorting={sorting}
            onSortingChange={setSorting}
            manualPagination={true}
            pageCount={Math.ceil((locationData?.data?.pagination?.total || 0) / pageSize)}
            pageIndex={page - 1}
            totalRecords={locationData?.data?.pagination?.total || 0}
            onPageChange={(newPageIndex) => setPage(newPageIndex + 1)}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
              setPage(1);
            }}
            pageSize={pageSize}
            onRowClick={(row) => {
              setSelectedLocationId(row.id);
              locationDetailsDrawerHandlers.open();
            }}
            columnPinning={{
              left: [],
              right: ["actions"],
            }}
            showTableCard={true}
            isLoading={showLoadingOverlay}
            tableCardProps={{
              columns: tableColumns,
              onColumnsChange: setTableColumns,
              onSearchChange: setSearchValue,
              onSortChange: handleSortChange,
              filterData,
              filterStoreType: "locations",
              isLoading: isFetching,
              initialSearchValue: searchParams.search,
              viewName,
              onApplyFilters: applyFilters,
              onResetFilters: resetFilters,
            }}
          />
        </Flex>
      </Flex>
      <LocationQuickViewDrawer
        opened={locationsDetailsDrawerOpened}
        onClose={locationDetailsDrawerHandlers.close}
        isLoading={overviewFetching}
        siteId={locationQuickViewData?.siteId ?? ""}
        onViewDetails={() => {
          if (selectedLocationId) {
            navigate({ to: "/locations/$id", params: { id: selectedLocationId } });
            locationDetailsDrawerHandlers.close();
          }
        }}
        locationType={locationQuickViewData?.locationType.label}
        badges={locationBadges}
        accessBadges={accessFeatureBadges}
        metrics={locationMetrics}
        address={
          locationQuickViewData?.locationAddress
            ? {
                address1: locationQuickViewData.locationAddress.address1,
                address2: locationQuickViewData.locationAddress.address2,
                city: locationQuickViewData.locationAddress.city,
                state: locationQuickViewData.locationAddress.state,
                zipcode: locationQuickViewData.locationAddress.zipcode,
                latitude: locationQuickViewData.locationAddress.latitude,
                longitude: locationQuickViewData.locationAddress.longitude,
              }
            : null
        }
        workOrders={workOrderSummary}
        upcomingVisitsTable={
          <UpcomingVisitsTable
            data={locationVisitsTableData || []}
            isLoading={locationVisitsTableLoading}
            selectedLocationId={selectedLocationId}
          />
        }
        upcomingVisitsEmptyMessage={
          !locationVisitsTableData?.length && !locationVisitsTableLoading
            ? "No upcoming visits scheduled"
            : undefined
        }
        onViewAllVisits={() => {
          locationDetailsDrawerHandlers.close();
          navigate({
            to: "/work-orders",
            search: {
              tab: "upcoming",
              ...(selectedLocationId
                ? {
                    filterBy: JSON.stringify({
                      locationId: [
                        {
                          id: selectedLocationId,
                          value:
                            locationQuickViewData?.locationAddress.fullAddress ??
                            selectedLocationId,
                        },
                      ],
                    }),
                  }
                : {}),
            },
          });
        }}
        invoices={invoiceSummary}
        addressSectionTitle="Location Address"
        invoicesSectionTitle="Invoices"
      />

      {/* Modals */}
      <AddOrEditLocationsModal
        openModal={locationModalOpened}
        setOpenModal={(open) => {
          if (!open) {
            locationModalHandlers.close();
            setModalClientId(undefined);
          }
        }}
        editingLocationId={modalLocationId}
        clientId={modalClientId ?? null}
        isLocationModule
        //Intentional prefix invalidation: since this query key is only used for table data, we can safely invalidate the entire list
        queryKeysToInvalidate={[LocationQueryKeys.list]}
      />

      <AssetsModal
        openModal={assetsModalOpened}
        setOpenModal={(open) => {
          if (!open) assetsModalHandlers.close();
        }}
        clientId={modalClientId ?? null}
        locationId={modalLocationId}
      />

      <ContactsModal
        openModal={contactsModalOpened}
        setOpenModal={(open) => {
          if (!open) {
            contactsModalHandlers.close();
            setPresetLocationsManaged([]);
            setModalClientId(undefined);
          }
        }}
        setAssets={() => {}}
        editingAsset={null}
        clientId={modalClientId ?? undefined}
        disableLocationsManaged={true}
        presetLocationsManaged={presetLocationsManaged}
      />

      <LocationStatusModal
        openModal={markAsInoperableModalOpened}
        closeModal={markAsInoperableModalHandlers.close}
        locationId={modalLocationId}
        siteId={locationStatusMeta.siteId}
        currentStatus={locationStatusMeta.currentStatus}
        queryKeysToInvalidate={[LocationQueryKeys.list, LocationQueryKeys.quickView]}
      />
    </Box>
  );
}

const mapLocationApiToLocation = (apiLocation: ILocationApiResponse): ILocations => ({
  id: apiLocation.id,
  siteId: apiLocation.siteId,
  clientId: apiLocation.clientId,
  clientName: apiLocation.clientName,
  status: {
    id: apiLocation.status.id,
    label: apiLocation.status.label,
    value: apiLocation.status.value as "operable" | "inoperable",
  },
  locationType: apiLocation.locationType?.label,
  brand: apiLocation.brand,
  activeWork: apiLocation.activeWorkOrdersCount,
  compliance: apiLocation.compliance,
  escalations: apiLocation.escalations,
  timezone: apiLocation.timezone,
  address: apiLocation.address,
  zipcode: apiLocation.zipcode,
  primaryContact: apiLocation.primaryContact,
});

// Maps the backend filterBy key → the column label used as the Zustand filter-store key.
// Must stay in sync with the searchParams.filterByKey values in the tableColumns config.
const LOCATIONS_FILTER_KEY_TO_LABEL: Record<string, string> = {
  siteId: "Site Id",
  status: "Status",
  locationType: "Location Type",
  clientName: "Client Name",
  brand: "Brand",
  activeWorkOrdersCount: "Active Work",
  timezone: "Timezone",
  address: "Address",
  zipcode: "Zip Code",
};

const getSortByField = (columnId: string): string | undefined => {
  const fieldMap: Record<string, string> = {
    siteId: "siteId",
    status: "status",
    locationType: "locationType",
    compliance: "compliance",
    brand: "brand",
    clientName: "clientName",
    activeWork: "activeWorkOrdersCount",
    timezone: "timezone",
    address: "address",
    zipcode: "zipcode",
  };
  return fieldMap[columnId];
};

const mapConfig = {
  Assets: { metricKey: "assetsCount", tab: "assets" },
  Contacts: { metricKey: "contactsCount", tab: "contacts" },
  Documents: { metricKey: "documentsCount", tab: "documents" },
  Escalations: { metricKey: "escalationsCount", tab: "escalations" },
};

type WorkOrdersKey = keyof IWorkOrders;

const workOrdersSummary: { label: string; key: WorkOrdersKey; tab: string }[] = [
  { label: "Active Work Orders", key: "activeWorkOrdersCount", tab: "active" },
  { label: "Completed Work Orders", key: "completedWorkOrdersCount", tab: "completed" },
];

type InvoicesKey = keyof NonNullable<ILocationQuickViewData["invoices"]>;

const paymentsAndInvoicesSummary: { key: InvoicesKey; subtitle: string; isAmount?: boolean }[] = [
  {
    key: "totalUnpaidInvoices",
    subtitle: "Total Unpaid Invoices",
  },
  {
    key: "totalUnpaidAmount",
    subtitle: "Total Unpaid Amount",
    isAmount: true,
  },
  {
    key: "dueInvoices",
    subtitle: "Due Invoices",
  },
  {
    key: "dueAmount",
    subtitle: "Due Amount",
    isAmount: true,
  },
  {
    key: "overdueInvoices",
    subtitle: "Overdue Invoices",
  },
  {
    key: "overdueAmount",
    subtitle: "Overdue Amount",
    isAmount: true,
  },
];
